import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Accordion, AccordionSummary, AccordionDetails, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useSnackbar } from 'notistack';
import * as eqpToolDiagApi from '../../lib/api/eqpToolDiag';
import {
  STATUS_LABEL, STATUS_COLOR, isPolling, flattenReportRows,
  buildScanParams, summarizeScanMacros, summarizeScanPmc,
} from './eqpToolDiagFormat';

const POLL_MS = 3000;

/**
 * (B) 공구/매거진 FOCAS 읽기 전용 진단 탭 - [공구 정보 진단] 버튼을 누르면 서버가 요청을 만들고,
 * 다음 에이전트 config 폴링에서 1회 실행된 결과를 3초마다 조회한다(discovery 다이얼로그와 같은 패턴).
 * 이 기능은 장비를 바꾸지 않는다(읽기 전용) - 매거진 대시보드가 아니라 "뭐가 읽히는지" 확인용이다.
 */
const EqpToolDiagTab = ({ eqpUuid }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [latest, setLatest] = useState(null);
  const [starting, setStarting] = useState(false);
  const statusRef = useRef(null);
  const timerRef = useRef(null);

  const loadLatest = useCallback(async () => {
    if (!eqpUuid) return;
    try {
      const res = await eqpToolDiagApi.getLatestToolDiag(eqpUuid);
      const info = eqpToolDiagApi.extractInfo(res);
      statusRef.current = info?.status || null;
      setLatest(info);
    } catch (e) {
      // 조용히 - 다음 주기에 다시
    }
  }, [eqpUuid]);

  useEffect(() => {
    loadLatest();
    return () => clearInterval(timerRef.current);
  }, [loadLatest]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (isPolling(statusRef.current)) loadLatest();
    }, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [loadLatest]);

  const runDiag = async (params) => {
    setStarting(true);
    try {
      const res = await eqpToolDiagApi.createToolDiag(eqpUuid, params);
      const info = eqpToolDiagApi.extractInfo(res);
      statusRef.current = info?.status || 'REQUESTED';
      setLatest(info);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || '이 장비에 FOCAS 채널/담당 에이전트가 있는지 확인하세요.';
      enqueueSnackbar(`진단을 시작하지 못했습니다. ${msg}`, { variant: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const handleRun = () => runDiag({});
  // 매거진이 어느 매크로/PMC 주소에 있는지 모를 때 - 넓은 구간을 한 번에 훑어 후보를 찾는다(§ 설계 노트).
  const handleScan = () => runDiag(buildScanParams());

  const status = latest?.status;
  let report = null;
  try {
    report = latest?.reportJson ? JSON.parse(latest.reportJson) : null;
  } catch (e) {
    report = null;
  }
  const rows = flattenReportRows(report);
  const scanMacros = summarizeScanMacros(report?.macros);
  const scanPmc = summarizeScanPmc(report?.pmc);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        장비 값을 읽기만 합니다(쓰기 없음). 어떤 FOCAS 항목이 이 장비에서 지원되는지 확인하는 용도입니다 - 매거진
        대시보드는 아직 제공하지 않습니다.
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={starting || isPolling(status) ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
          disabled={starting || isPolling(status)}
          onClick={handleRun}
        >
          공구 정보 진단 실행
        </Button>
        <Button
          variant="outlined"
          startIcon={starting || isPolling(status) ? <CircularProgress size={16} /> : <TravelExploreIcon />}
          disabled={starting || isPolling(status)}
          onClick={handleScan}
        >
          매거진 위치 찾기(스캔)
        </Button>
        {status && <Chip size="small" color={STATUS_COLOR[status] || 'default'} label={STATUS_LABEL[status] || status} />}
        {latest?.errMsg && <Typography variant="caption" color="error.main">{latest.errMsg}</Typography>}
      </Box>

      {(scanMacros.length > 0 || scanPmc.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">매거진 후보(값이 있는 주소만)</Typography>
          {scanMacros.length > 0 && (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              매크로: {scanMacros.join(', ')}
            </Typography>
          )}
          {scanPmc.length > 0 && (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              PMC: {scanPmc.join(', ')}
            </Typography>
          )}
        </Box>
      )}

      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>항목</TableCell>
                <TableCell>결과</TableCell>
                <TableCell align="center">상태</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-all' }}>{r.detail}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" color={r.ok ? 'success' : 'default'} variant={r.ok ? 'filled' : 'outlined'}
                      label={r.ok ? 'OK' : '실패'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {report && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">원본 JSON 보기</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{
              m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              maxHeight: 360, overflowY: 'auto',
            }}>
              {JSON.stringify(report, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {!report && !isPolling(status) && latest && (
        <Typography variant="body2" color="text.secondary">보고된 결과가 없습니다.</Typography>
      )}
    </Box>
  );
};

export default EqpToolDiagTab;
